import React from 'react';
import { Grid, ToggleButtonGroup } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useLanguage } from '../../utils/GeneralUtils';
import { OrderPageTypography } from '../themedComponents/OrderPageTypography';
import { OrderPageData, OrderPageTypes } from '../../api';
import { useAccountData } from '../components/AccountSpecificThemeWrapper';
import { OrderPageToggleButton } from '../themedComponents/OrderPageToggleButton';
import { getOrderPageLabel } from '../../utils/AccountUtils';

type MultiOrderPagePickerProps = {
  orderPages: OrderPageData[];
  initialOrderPageIds: string[];
  selectedOrderPageIds: string[];
  setSelectedOrderPageIds: (orderPageIds: string[]) => void;
};

export const MultiOrderPagePicker = ({
  orderPages,
  initialOrderPageIds,
  selectedOrderPageIds,
  setSelectedOrderPageIds,
}: MultiOrderPagePickerProps) => {
  const account = useAccountData();
  const language = useLanguage();
  const { t } = useTranslation();
  if (orderPages.length === 0) {
    return null;
  }
  const handleToggleChange = (event: React.MouseEvent<HTMLElement>, newSelectedIds: string[]) => {
    setSelectedOrderPageIds(newSelectedIds || []);
  };

  if (!initialOrderPageIds || initialOrderPageIds.length <= 1) {
    return null;
  }
  const sortedOrderPageIds = initialOrderPageIds.sort((a, b) => {
    const orderPageA =
      orderPages.find((orderPage) => orderPage.id === a)!.order_page_type === OrderPageTypes.PRIVATE ? 0 : 1;
    const orderPageB =
      orderPages.find((orderPage) => orderPage.id === b)!.order_page_type === OrderPageTypes.PRIVATE ? 0 : 1;
    return orderPageA < orderPageB ? -1 : 1;
  });
  return (
    <Grid container direction="column">
      <Grid item mt={2}>
        <OrderPageTypography variant="h3" bold>
          {t('multipleOrderPage.multiselecttitle', {
            activityLabel: account?.activity_label_plural?.[language]?.toLowerCase(),
          })}
        </OrderPageTypography>
      </Grid>
      <ToggleButtonGroup
        value={selectedOrderPageIds}
        onChange={handleToggleChange}
        sx={{
          flexWrap: 'wrap',
          gap: 2,
          mt: 2,
          '& .MuiToggleButtonGroup-grouped': {
            margin: 0,
            border: 'none',
            '&:not(:first-of-type)': {
              borderRadius: '12px',
            },
            '&:first-of-type': {
              borderRadius: '12px',
            },
          },
        }}>
        {sortedOrderPageIds.map((orderPageId: string) => (
          <OrderPageToggleButton
            key={orderPageId}
            value={orderPageId}
            selected={selectedOrderPageIds.includes(orderPageId)}
            sx={{
              minWidth: '250px',
              maxWidth: '250px',
              minHeight: '60px',
              display: 'flex',
              justifyContent: 'flex-start',
              alignItems: 'center',
              borderRadius: '12px',
              textTransform: 'none',
              border: '2px solid',
              borderColor: (theme) => theme.customTheme.palette.border,
              boxShadow: '0 4px 12px rgba(0,0,0,0.15), 0 2px 4px rgba(0,0,0,0.1)',
              transition: 'all 0.2s ease-in-out',
              '&:hover': {
                transform: 'translateY(-2px)',
                boxShadow: '0 8px 20px rgba(0,0,0,0.2), 0 4px 8px rgba(0,0,0,0.15)',
                borderColor: (theme) => theme.customTheme.palette.primary,
              },
              '&.Mui-selected': {
                backgroundColor: (theme) => theme.customTheme.palette.primary,
                color: (theme) => theme.customTheme.palette.text.primary,
                borderColor: (theme) => theme.customTheme.palette.primary,
                '& .MuiTypography-root': {
                  color: 'white !important',
                },
                '&:hover': {
                  backgroundColor: (theme) => theme.customTheme.palette.primary,
                  transform: 'translateY(-2px)',
                  boxShadow: '0 8px 20px rgba(0,0,0,0.2), 0 4px 8px rgba(0,0,0,0.15)',
                  '& .MuiTypography-root': {
                    color: `${(theme: any) => theme.customTheme.palette.text.primary} !important`,
                  },
                },
              },
            }}>
            <OrderPageTypography
              variant="h6"
              style={{
                display: '-webkit-box',
                WebkitBoxOrient: 'vertical',
                WebkitLineClamp: 2,
                overflow: 'hidden',
                lineHeight: 1.2,
              }}>
              {getOrderPageLabel(language, orderPages, orderPageId)}
            </OrderPageTypography>
          </OrderPageToggleButton>
        ))}
      </ToggleButtonGroup>
    </Grid>
  );
};
